'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgChevronright, SvgPointCoupon, SvgPointStore, SvgWelmarket } from '@common/icon';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES, WEL_MARKET_ROUTES } from '@/constants';
import { useIsGuest } from '@/hooks/useIsGuest';
const BalanceFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="font-700 text-Gray-800 typo-body1">-</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const PointBalanceAmount = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...AuthQueryOptions["get:/wello/v2/auth/point"](),
      select: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: totalPoints
  } = useSuspenseQuery(t0);
  let t1;
  if ($[1] !== totalPoints) {
    t1 = totalPoints.toLocaleString();
    $[1] = totalPoints;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <>{t1}</>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const POINT_BOX_CLASS_NAME = 'flex flex-1 items-center justify-between gap-16 rounded-small border border-Gray-100 bg-Gray-white p-16 text-left transition-colors xl:flex-col xl:items-stretch xl:hover:border-Gray-300 xl:hover:bg-Gray-50 focus-visible:border-Gray-300 focus-visible:bg-Gray-50 active:bg-Gray-50';
const SHORTCUT_CLASS_NAME = 'group flex h-60 flex-1 items-center gap-10 rounded-small border border-Gray-100 bg-Gray-white p-16 transition-colors xl:h-auto xl:hover:border-Gray-300 xl:hover:bg-Gray-50 focus-visible:border-Gray-300 focus-visible:bg-Gray-50 active:bg-Gray-50';

// button/anchor 내부에 들어가므로 phrasing content(span)만 사용한다.
const PointBoxContent = t0 => {
  const $ = _c(9);
  const {
    isGuest
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="flex items-center gap-8"><SvgPointCoupon className="size-24" originalFill={true} /><span className="font-700 text-Gray-900 typo-body4">웰포인트</span></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = isGuest ? "text-Gray-300" : "text-Gray-800";
  let t3;
  if ($[1] !== t2) {
    t3 = cn("flex items-center justify-end gap-8 text-right font-700 typo-body1", t2);
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isGuest) {
    t4 = isGuest ? <span aria-label={"\uB85C\uADF8\uC778 \uD6C4 \uD655\uC778 \uAC00\uB2A5"}>???</span> : <ErrorBoundary fallback={<>-</>}><Suspense fallback={<BalanceFallback />}><PointBalanceAmount /></Suspense></ErrorBoundary>;
    $[3] = isGuest;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span>p</span>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t3 || $[7] !== t4) {
    t6 = <>{t1}<span className={t3}>{t4}{t5}</span></>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
interface WelloPointCardProps {
  isGuest: boolean;
}
export const WelloPointCard = t0 => {
  const $ = _c(15);
  const {
    isGuest: serverIsGuest
  } = t0;
  const isGuest = useIsGuest(serverIsGuest);
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname) {
    t1 = ROUTES.LOGIN.withSearchParams({
      searchParams: {
        redirect: pathname ?? ""
      }
    });
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const loginHref = t1;
  let t2;
  if ($[2] !== isGuest || $[3] !== loginHref) {
    t2 = isGuest ? <CustomLink href={loginHref} className="group flex w-fit flex-col gap-4 text-left"><span className="font-800 text-Gray-900 underline underline-offset-4 transition-colors typo-body1 group-focus-visible:text-Gray-600 group-active:text-Gray-600 xl:group-hover:text-Gray-600">로그인 / 회원 가입하기</span><span className="text-Gray-600 typo-caption1">웰로 회원은 웰포인트 적립 혜택을 누릴 수 있어요</span></CustomLink> : null;
    $[2] = isGuest;
    $[3] = loginHref;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isGuest || $[6] !== loginHref) {
    t3 = isGuest ? <CustomLink href={loginHref} className={POINT_BOX_CLASS_NAME}><PointBoxContent isGuest={true} /></CustomLink> : <CustomLink href={ROUTES.POINTS.pathname} className={POINT_BOX_CLASS_NAME}><PointBoxContent isGuest={false} /></CustomLink>;
    $[5] = isGuest;
    $[6] = loginHref;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CustomLink href={WEL_MARKET_ROUTES.HOME.pathname} className={SHORTCUT_CLASS_NAME}><SvgWelmarket originalFill={true} className="size-24" /><span className="flex-1 font-700 text-Gray-900 typo-body4">웰마켙</span><SvgChevronright className="size-16 shrink-0 text-Gray-400 transition group-focus-visible:translate-x-1 group-focus-visible:text-Gray-600 xl:group-hover:translate-x-1 xl:group-hover:text-Gray-600" /></CustomLink>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex flex-1 gap-8 xl:flex-col">{t4}<CustomLink href={ROUTES.POINT_MARKET.pathname} className={SHORTCUT_CLASS_NAME}><SvgPointStore className="size-24" originalFill={true} /><span className="flex-1 font-700 text-Gray-900 typo-body4">웰포인트상점</span><SvgChevronright className="size-16 shrink-0 text-Gray-400 transition group-focus-visible:translate-x-1 group-focus-visible:text-Gray-600 xl:group-hover:translate-x-1 xl:group-hover:text-Gray-600" /></CustomLink></div>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3) {
    t6 = <div className="flex flex-col gap-8 xl:flex-row">{t3}{t5}</div>;
    $[10] = t3;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t6) {
    t7 = <section className="flex flex-col gap-12 bg-Gray-white p-20 xl:bg-transparent xl:p-0">{t2}{t6}</section>;
    $[12] = t2;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
function _temp(data) {
  return data?.context?.totalPoints ?? 0;
}